import { getCurrentUser } from "@/lib/auth";
import { db } from "@/lib/firebase";
import { Organization } from "@/types/organization";
import { doc, DocumentReference, serverTimestamp, updateDoc } from "firebase/firestore";

/**
 * Helper function to get the current user reference
 */
const getUserReference = (): DocumentReference => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error("No authenticated user");
  }
  return doc(db, `users/${currentUser.uid}`);
};

/**
 * Updates an existing organization's name and description, and updates the user's name in userPermissions
 * @param organizationId - The ID of the organization to update
 * @param name - The organization name
 * @param description - Optional organization description
 * @param firstName - The user's first name
 * @param lastName - The user's last name
 * @returns Promise that resolves when the update is complete
 */
export const updateOrganization = async (
  organizationId: string,
  name: string,
  firstName: string,
  lastName: string,
  description?: string
): Promise<void> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error("No authenticated user");
    }

    const userRef = getUserReference();
    const orgRef = doc(db, `organizations/${organizationId}`);

    // Update organization document
    const updateData: Partial<Organization> = {
      name,
      description: description || "",
      updatedAt: serverTimestamp() as any,
      updatedBy: userRef,
    };

    await updateDoc(orgRef, updateData);

    // Update user's name in userPermissions
    const userPermissionRef = doc(db, `organizations/${organizationId}/userPermissions/${currentUser.uid}`);
    await updateDoc(userPermissionRef, {
      firstName,
      lastName,
      updatedAt: serverTimestamp() as any,
      updatedBy: userRef,
    });
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};
